import React, { useEffect, useMemo, useCallback, useState } from "react";
import { Box, Button, useMediaQuery } from "@mui/material";
import { Close as CloseIcon, ViewModuleOutlined } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { getMapViewData, getProviderSettings, setSelectedCity } from "../customerServiceBoardSlice";
import { MapLocation, MapViewQuery, ServiceBoardState } from "../models/models";
import MapContainerComponent, { ZOOM_THRESHOLD } from "./map/MapContainer";
import { CompanyGroup } from "./map/RfsMarker";
import CitySidebar from "./map/CitySidebar";
import useTranslation from "../../../shared/hooks/useTranslation";
import useDataRefresh from "../../../shared/notifications/hooks/useDataRefresh";
import useVisibilityRefresh from "../../../shared/hooks/useVisibilityRefresh";
import { dt } from "../../../shared/themes/redesignTokens";

// ===================================================================
//  DESIGN TOKENS
// ===================================================================

// ===================================================================
//  COMPANY GROUPING: Build one marker per company from flat requests
// ===================================================================

const SPREAD_RADIUS = 0.003; // base offset in degrees (~300m at equator)

const buildCompanyGroups = (
  locations: MapLocation[],
  zoomLevel: number
): CompanyGroup[] => {
  // Group all requests by company ID across all locations
  const companyMap = new Map<
    string,
    {
      companyId: string;
      companyName: string;
      logoUrl?: string;
      lat: number;
      lng: number;
      requests: import("../models/models").MapServiceRequest[];
      city: string;
      countryName: string;
      countryId: number;
    }
  >();

  for (const loc of locations) {
    for (const req of loc.requests) {
      const companyId = req.customer.id;
      const existing = companyMap.get(companyId);
      if (existing) {
        existing.requests.push(req);
      } else {
        // Use per-request customer coordinates if available, else fall back to city coords
        const lat = req.customer.latitude ?? loc.latitude;
        const lng = req.customer.longitude ?? loc.longitude;
        companyMap.set(companyId, {
          companyId,
          companyName: req.customer.name,
          logoUrl: req.customer.logoUrl,
          lat,
          lng,
          requests: [req],
          city: loc.city,
          countryName: loc.countryName,
          countryId: loc.countryId,
        });
      }
    }
  }

  const groups = Array.from(companyMap.values());

  // Apply fan-out for companies that share the same coordinates
  return applyCompanyFanOut(groups, zoomLevel);
};

/**
 * Spread co-located company markers in a circle (same logic as old applyFanOut,
 * but operates on CompanyGroup[] instead of FlatRequest[]).
 */
const applyCompanyFanOut = (
  groups: CompanyGroup[],
  zoomLevel: number
): CompanyGroup[] => {
  const coordGroups = new Map<string, CompanyGroup[]>();
  for (const cg of groups) {
    const key = `${cg.lat.toFixed(6)}_${cg.lng.toFixed(6)}`;
    const existing = coordGroups.get(key);
    if (existing) {
      existing.push(cg);
    } else {
      coordGroups.set(key, [cg]);
    }
  }

  const result: CompanyGroup[] = [];
  const zoomFactor = Math.pow(2, zoomLevel - ZOOM_THRESHOLD);

  for (const group of Array.from(coordGroups.values())) {
    if (group.length === 1) {
      result.push(group[0]);
    } else {
      const count = group.length;
      const effectiveRadius = SPREAD_RADIUS / zoomFactor;
      for (let i = 0; i < count; i++) {
        const angle = (2 * Math.PI / count) * i;
        const offsetLat = Math.cos(angle) * effectiveRadius;
        const offsetLng = Math.sin(angle) * effectiveRadius;
        result.push({
          ...group[i],
          lat: group[i].lat + offsetLat,
          lng: group[i].lng + offsetLng,
        });
      }
    }
  }

  return result;
};

// ===================================================================
//  COMPONENT
// ===================================================================

interface ServiceBoardMapViewProps {
  filters?: MapViewQuery;
  onClose?: () => void;
}

const ServiceBoardMapView: React.FC<ServiceBoardMapViewProps> = ({ filters, onClose }) => {
  const { translate } = useTranslation(["serviceBoard", "common"]);
  const dispatch = useAppDispatch();
  const isPhone = useMediaQuery("(max-width:768px)");
  const isTablet = useMediaQuery("(max-width:1024px)");

  const { mapData, mapLoading, selectedCity, providerSettings } =
    useAppSelector<ServiceBoardState>((state: any) => state.customerServiceBoard);

  // Local UI state
  const [zoomLevel, setZoomLevel] = useState(7);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  /** Virtual MapLocation built when a company marker is clicked (zoomed in) */
  const [virtualSidebarLocation, setVirtualSidebarLocation] = useState<MapLocation | null>(null);

  // Fetch map data and provider settings on mount
  useEffect(() => {
    dispatch(getMapViewData(filters ?? {}));
  }, [dispatch, filters]);

  useEffect(() => {
    dispatch(getProviderSettings());
  }, [dispatch]);

  // Real-time: silently refresh map data when Service Board changes
  useDataRefresh("serviceBoard", () => dispatch(getMapViewData(filters ?? {})));
  useVisibilityRefresh(() => dispatch(getMapViewData(filters ?? {})));

  // ---------------------------------------------------------------
  //  ZOOMED-OUT: City marker click -> open sidebar for that location
  //  We store the clicked MapLocation directly (it may be a merged cluster
  //  containing requests from multiple original locations) so the sidebar
  //  shows ALL requests, not just the first matching unclustered location.
  // ---------------------------------------------------------------
  const handleMarkerClick = useCallback(
    (location: MapLocation) => {
      setSelectedCompanyId(null);

      // Toggle: clicking the same city again closes the sidebar
      if (location.city === selectedCity) {
        setVirtualSidebarLocation(null);
        dispatch(setSelectedCity(null));
        return;
      }

      // Store the full (possibly merged) MapLocation for the sidebar
      setVirtualSidebarLocation(location);
      dispatch(setSelectedCity(location.city));
    },
    [dispatch, selectedCity]
  );

  // ---------------------------------------------------------------
  //  ZOOMED-IN: Company marker click -> build virtual MapLocation for sidebar
  // ---------------------------------------------------------------
  const handleCompanyMarkerClick = useCallback(
    (companyGroup: CompanyGroup) => {
      // Toggle: clicking the same company again closes the sidebar
      if (selectedCompanyId === companyGroup.companyId) {
        setSelectedCompanyId(null);
        setVirtualSidebarLocation(null);
        dispatch(setSelectedCity(null));
        return;
      }

      setSelectedCompanyId(companyGroup.companyId);

      // Build a virtual MapLocation so the sidebar can display this company's requests
      // Priority codes: 1=urgent, 2=high, 4=low
      const requests = companyGroup.requests;
      const urgentCount = requests.filter((r) => r.priorityTypeCode === 1).length;
      const highCount = requests.filter((r) => r.priorityTypeCode === 2).length;
      const lowCount = requests.filter((r) => r.priorityTypeCode === 4).length;

      const virtualLocation: MapLocation = {
        city: companyGroup.companyName,
        countryName: companyGroup.countryName,
        countryId: companyGroup.countryId,
        latitude: companyGroup.lat,
        longitude: companyGroup.lng,
        totalCount: requests.length,
        urgentCount,
        highCount,
        lowCount,
        requests,
      };

      setVirtualSidebarLocation(virtualLocation);
      // Set selectedCity to the company name so CityMarker selection highlight works
      dispatch(setSelectedCity(companyGroup.companyName));
    },
    [dispatch, selectedCompanyId]
  );

  const handleSidebarClose = useCallback(() => {
    dispatch(setSelectedCity(null));
    setSelectedCompanyId(null);
    setVirtualSidebarLocation(null);
  }, [dispatch]);

  const handleZoomChanged = useCallback((zoom: number) => {
    setZoomLevel(zoom);
    // Clear sidebar when crossing zoom threshold to avoid stale data
    if (
      (zoom >= ZOOM_THRESHOLD && zoomLevel < ZOOM_THRESHOLD) ||
      (zoom < ZOOM_THRESHOLD && zoomLevel >= ZOOM_THRESHOLD)
    ) {
      setSelectedCompanyId(null);
      setVirtualSidebarLocation(null);
      dispatch(setSelectedCity(null));
    }
  }, [dispatch, zoomLevel]);

  // Provider effective location
  const providerLocation = useMemo(() => {
    if (
      providerSettings?.effectiveLatitude != null &&
      providerSettings?.effectiveLongitude != null
    ) {
      return {
        lat: providerSettings.effectiveLatitude,
        lng: providerSettings.effectiveLongitude,
      };
    }
    return null;
  }, [providerSettings]);

  // All locations from the map data (no distance filtering)
  const sortedLocations = useMemo(() => {
    return mapData?.locations ?? [];
  }, [mapData]);

  // Build company groups for zoomed-in mode
  const companyGroups = useMemo(() => {
    return buildCompanyGroups(sortedLocations, zoomLevel);
  }, [sortedLocations, zoomLevel]);

  const summary = mapData?.summary;

  const effectiveSummary = summary;

  // Sidebar location: either virtual (from company click) or from city selection
  const selectedLocation = useMemo(() => {
    // If we have a virtual sidebar location (from zoomed-in company click), use it
    if (virtualSidebarLocation) return virtualSidebarLocation;

    // Otherwise, find by city name for zoomed-out mode
    if (!selectedCity || sortedLocations.length === 0) return null;

    // Check original locations (not clustered) — also check _originalCities on clusters
    return sortedLocations.find((loc) => {
      if (loc.city === selectedCity) return true;
      // Check if this is a merged cluster containing the selected city
      const originalCities: string[] | undefined = (loc as any)._originalCities;
      if (originalCities && originalCities.includes(selectedCity)) return true;
      return false;
    }) || null;
  }, [selectedCity, sortedLocations, virtualSidebarLocation]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        animation: "mapFadeIn 0.4s ease both",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Map stats bar + controls */}
      {effectiveSummary && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            px: "16px",
            py: "10px",
            backgroundColor: dt.cardBg,
            borderRadius: dt.radiusSm,
            border: `1px solid ${dt.cardBorder}`,
            boxShadow: dt.cardShadow,
            flexShrink: 0,
            animation: "headerReveal 0.4s ease 0.1s both",
            ...(isPhone && {
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              gap: 1,
              px: "12px",
              py: "8px",
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }),
          }}
        >
          {/* Left: stats */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
            <StatPill
              label={translate("mapLocations")}
              value={effectiveSummary.totalLocations}
              color={dt.blueHover}
            />
            <StatPill
              label={translate("mapRequests")}
              value={effectiveSummary.totalRequests}
              color="#27AE60"
            />
            <StatPill
              label={translate("mapUrgent")}
              value={effectiveSummary.urgentRequests}
              color="#C0392B"
            />
          </Box>

          {/* Right: Board View button */}
          {onClose && (
            <Button
              variant="outlined"
              startIcon={<ViewModuleOutlined sx={{ fontSize: "16px !important" }} />}
              onClick={onClose}
              sx={{
                borderRadius: dt.radiusPill,
                borderColor: dt.cardBorder,
                color: dt.textSecondary,
                fontFamily: dt.font,
                fontWeight: 600,
                fontSize: "12px",
                textTransform: "none",
                padding: "5px 14px",
                whiteSpace: "nowrap",
                flexShrink: 0,
                ml: "auto",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: dt.blue,
                  color: dt.blue,
                  backgroundColor: dt.blueGlow,
                  transform: "translateY(-1px)",
                },
              }}
            >
              {translate("boardViewButton")}
            </Button>
          )}
        </Box>
      )}

      {/* Map container */}
      <Box
        sx={{
          backgroundColor: dt.cardBg,
          borderRadius: isPhone ? 0 : dt.radiusLg,
          border: isPhone ? "none" : `1px solid ${dt.cardBorder}`,
          boxShadow: isPhone ? "none" : dt.cardShadow,
          overflow: "hidden",
          height: isPhone ? "calc(100vh - 140px)" : undefined,
          flex: isPhone ? undefined : 1,
          minHeight: isPhone ? "320px" : "400px",
          position: "relative",
          animation: "mapFadeIn 0.5s ease 0.15s both",
          ...(isPhone && {
            mx: "-16px",
          }),
        }}
      >
        {sortedLocations.length === 0 && !mapLoading && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 5,
              backgroundColor: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(6px)",
              borderRadius: dt.radiusSm,
              padding: "16px 24px",
              textAlign: "center",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <Box
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                fontFamily: dt.font,
                color: dt.textPrimary,
              }}
            >
              {translate("mapNoData")}
            </Box>
            <Box
              sx={{
                fontSize: "12px",
                fontFamily: dt.font,
                color: dt.textSecondary,
                mt: "4px",
              }}
            >
              {translate("tryAdjustingFilters")}
            </Box>
          </Box>
        )}

        <MapContainerComponent
          locations={sortedLocations}
          companyGroups={companyGroups}
          selectedCity={selectedCity}
          selectedCompanyId={selectedCompanyId}
          onMarkerClick={handleMarkerClick}
          onCompanyMarkerClick={handleCompanyMarkerClick}
          onZoomChanged={handleZoomChanged}
          loading={mapLoading}
          providerLocation={providerLocation}
        />

        {/* Close map view button (floating, top-right) */}
        {onClose && (
          <Box
            onClick={onClose}
            sx={{
              position: "absolute",
              top: "12px",
              right: "12px",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              gap: "6px",
              backgroundColor: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(8px)",
              borderRadius: dt.radiusPill,
              padding: "6px 16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              cursor: "pointer",
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 600,
              color: dt.textSecondary,
              border: `1px solid ${dt.cardBorder}`,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#FFFFFF",
                color: dt.textPrimary,
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: "16px" }} />
            {translate("common:buttons.close")}
          </Box>
        )}

        {/* City sidebar (overlays on right side of map) */}
        <CitySidebar
          location={selectedLocation}
          onClose={handleSidebarClose}
          mode={isPhone ? "fullscreen" : isTablet ? "bottomSheet" : "side"}
        />
      </Box>
    </Box>
  );
};

// ===================================================================
//  STAT PILL (inline helper)
// ===================================================================

interface StatPillProps {
  label: string;
  value: number;
  color: string;
}

const StatPill: React.FC<StatPillProps> = React.memo(({ label, value, color }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
    <Box
      sx={{
        fontSize: "11px",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.6px",
        color: "#9CA3AF",
        fontFamily: 'var(--font-sans)',
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontSize: "16px",
        fontWeight: 700,
        fontFamily: 'var(--font-mono)',
        color: color,
        letterSpacing: "-0.3px",
      }}
    >
      {value}
    </Box>
  </Box>
));

export default React.memo(ServiceBoardMapView);
